import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, AlertTriangle } from 'lucide-react';
import { paymentAPI } from '../utils/api';
import toast from 'react-hot-toast';

export const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'failed' | 'error'>('loading');
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get order ID from session storage or URL params
        const orderId = sessionStorage.getItem('pendingOrderId') || searchParams.get('orderId');
        
        if (!orderId) {
          setStatus('error');
          toast.error('Order ID not found');
          setTimeout(() => navigate('/orders'), 3000);
          return;
        }

        // Get payment status from URL params (sent by PayGlocal)
        const paymentStatus = searchParams.get('status');
        const transactionId = searchParams.get('gid') || searchParams.get('transactionId');

        console.log('Payment callback received:', {
          orderId,
          paymentStatus,
          transactionId,
          allParams: Object.fromEntries(searchParams.entries())
        });

        // Verify payment status from backend
        const response = await paymentAPI.checkPaymentStatus(orderId);
        console.log('Payment verification response:', response);

        setPaymentDetails({
          orderId,
          status: response.data.paymentStatus,
          transactionId: response.data.transactionId,
          amount: response.data.amount,
        });

        // Clear pending order from session
        sessionStorage.removeItem('pendingOrderId');

        // Set status based on payment verification
        if (response.data.paymentStatus === 'success' || 
            response.data.paymentStatus === 'completed' ||
            response.data.orderStatus === 'confirmed') {
          setStatus('success');
          toast.success('Payment successful! Your order has been confirmed.');
        } else if (response.data.paymentStatus === 'failed' || 
                   response.data.paymentStatus === 'cancelled') {
          setStatus('failed');
          toast.error('Payment failed. Please try again.');
        } else {
          // Payment is still pending verification
          setStatus('loading');
          // Check again after 3 seconds
          setTimeout(() => {
            verifyPayment();
          }, 3000);
          return;
        }

        // Redirect to orders page after 5 seconds
        setTimeout(() => {
          navigate('/orders');
        }, 5000);

      } catch (error: any) {
        console.error('Payment verification error:', error);
        setStatus('error');
        toast.error('Failed to verify payment status');
        setTimeout(() => navigate('/orders'), 3000);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <Loader className="h-16 w-16 text-primary-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verifying Payment
              </h2>
              <p className="text-gray-600">
                Please wait while we confirm your payment...
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && paymentDetails && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-green-100 rounded-full p-4">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Your order has been confirmed and will be processed shortly.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="font-semibold">{paymentDetails.orderId}</span>
                  </div>
                  {paymentDetails.transactionId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transaction ID:</span>
                      <span className="font-semibold text-sm">{paymentDetails.transactionId}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className="font-semibold">₹{paymentDetails.amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Redirecting to your orders...
              </p>
              <button
                onClick={() => navigate('/orders')}
                className="btn-primary w-full"
              >
                View Orders
              </button>
            </div>
          )}

          {/* Failed State */}
          {status === 'failed' && paymentDetails && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-red-100 rounded-full p-4">
                  <XCircle className="h-16 w-16 text-red-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Failed
              </h2>
              <p className="text-gray-600 mb-6">
                Your payment could not be processed. Please try again.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="font-semibold">{paymentDetails.orderId}</span>
                  </div>
                  {paymentDetails.transactionId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transaction ID:</span>
                      <span className="font-semibold text-sm">{paymentDetails.transactionId}</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                You can retry payment from your orders page.
              </p>
              <button
                onClick={() => navigate('/orders')}
                className="btn-primary w-full"
              >
                Go to Orders
              </button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="bg-yellow-100 rounded-full p-4">
                  <AlertTriangle className="h-16 w-16 text-yellow-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Unable to Verify Payment
              </h2>
              <p className="text-gray-600 mb-6">
                We couldn't verify your payment status. Please check your orders page or contact support.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Redirecting to orders...
              </p>
              <button
                onClick={() => navigate('/orders')}
                className="btn-primary w-full"
              >
                View Orders
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

